import React from 'react';
import styled from '@emotion/styled';
import { Checkbox } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SeminarCard, { SeminarCardData } from '@/components/domain/seminar/SeminarCard';
import Button from '@/components/common/elements/Button';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { designSystem } from '@/styles/theme';
import { FORM_COLOR, GuideLines } from '@/pages/Seminar/components/form/formStyles';
import { STEP_META, SeminarFormStep } from '@/pages/Seminar/components/form/seminarFormModel';
import type { SeminarStatusLiteral } from '@/pages/Seminar/types';
import { useSeminarForm } from '@/pages/Seminar/components/form/steps/SeminarFormContext';

// ─── 목록 카드 미리보기 ──────────────────────────────────────────────────

const PreviewFrame = styled.div`
  /* 미리보기라 누를 수 없게 — 카드 자체의 링크 동작은 막는다 */
  pointer-events: none;
  user-select: none;
`;

/** 입력한 값으로 목록 카드를 그대로 그린다(기존 SeminarCard) */
export const SeminarFormPreview: React.FC<{ width?: number | string }> = ({ width = 200 }) => {
  const { values, career, format, schedule, openSale, scheduleLabel } = useSeminarForm();
  const capacity: number | undefined = Number(values.capacity) >= 1 ? Number(values.capacity) : undefined;
  const status: SeminarStatusLiteral = 'PUBLISHED';
  const data: SeminarCardData = {
    id: 'preview',
    title: values.title?.trim() || '세미나 제목',
    thumbnailUrl: values.thumbnailUrl || undefined,
    seminarType: career ? 'CAREER' : 'GENERAL',
    format: career ? format : undefined,
    schedule: career ? schedule : undefined,
    isOnline: !career && !!values.isOnline,
    scheduleLabel: scheduleLabel ?? undefined,
    location: { address: values.address || '', fullAddress: values.address || '' },
    // 참가비를 아직 안 넣었으면 값을 비워 카드가 '참가비 입력 전'을 보이게(0 은 무료로 그대로)
    participationFee: values.price == null || String(values.price) === '' ? undefined : Number(values.price) || 0,
    instructorNickname: values.instructor || '',
    status,
    maxParticipants: capacity ?? 0,
    currentParticipants: 0,
    remainingSeats: career ? (openSale && !capacity ? null : capacity ?? null) : undefined,
    recruitmentEndDateTime: openSale ? undefined : values.recruitmentEndDateTime || undefined,
  };
  return (
    <PreviewFrame aria-label="목록 카드 미리보기" style={{ width }}>
      <SeminarCard seminar={data} onClick={() => undefined} emptyPriceText="참가비 입력 전" />
    </PreviewFrame>
  );
};

// ─── 단계별 완료 체크 ───────────────────────────────────────────────────

const CheckBox = styled.div`
  padding: 6px 14px;
  border: 1px solid ${FORM_COLOR.line};
  border-radius: 14px;
  background: #fff;
`;

const CheckRow = styled.div<{ tone: 'ok' | 'error' | 'soft' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 0;
  border-bottom: 1px solid ${FORM_COLOR.lineSoft};
  font-size: 14.5px;
  line-height: 1.45;
  color: ${({ tone }) => (tone === 'error' ? FORM_COLOR.dangerText : tone === 'soft' ? '#92400e' : FORM_COLOR.text)};
  word-break: keep-all;

  &:last-child {
    border-bottom: 0;
  }

  > svg {
    flex: 0 0 auto;
    font-size: 21px;
    color: ${({ tone }) => (tone === 'error' ? FORM_COLOR.danger : tone === 'soft' ? '#d97706' : FORM_COLOR.primary)};
  }

  > span {
    flex: 1;
    min-width: 0;
  }
`;

const FillButton = styled.button`
  flex: 0 0 auto;
  min-height: 36px;
  padding: 0 4px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  color: ${FORM_COLOR.primaryDeep};
  cursor: pointer;
`;

interface CheckItem {
  step: SeminarFormStep;
  label: string;
}

/** 단계별 완료 체크(폰 확인 단계) — 다 채운 단계는 초록 체크, 빠진 단계는 빨간 줄(첫 빠진 항목 문구) + [채우기] */
export const StepChecklist: React.FC = () => {
  const { stepProblems, career, paymentEnabled, isOnlineFormat, showAddress, goToField } = useSeminarForm();
  const items: CheckItem[] = [
    { step: 1, label: career ? '유형·제목·분야·대상' : paymentEnabled ? '유형·제목' : '제목' },
    { step: 2, label: `진행 방식·일정${isOnlineFormat ? '·접속 링크' : showAddress ? '·장소' : ''}` },
    { step: 3, label: career ? '참가비·정원·모집 기간' : '참가비' },
    { step: 4, label: '설명' },
  ];
  return (
    <CheckBox role="list" aria-label="입력 확인">
      {items.map((item) => {
        const problem = stepProblems[item.step];
        return problem ? (
          <CheckRow key={item.step} tone="error" role="listitem">
            <ErrorOutlineIcon aria-hidden />
            <span>{problem.message.replace(/\n/g, ' ')}</span>
            <FillButton type="button" onClick={() => goToField(problem.field)}>채우기</FillButton>
          </CheckRow>
        ) : (
          <CheckRow key={item.step} tone="ok" role="listitem">
            <CheckCircleOutlineIcon aria-hidden />
            <span>{item.label}</span>
          </CheckRow>
        );
      })}
    </CheckBox>
  );
};

// ─── PC 오른쪽 단계 목록(단계 이동 + 빠진 항목 통합, 2026-09-29 사용자 피드백) ────────────

/** 빠진 칸 → 짧은 이름(같은 이름은 한 번만) */
const FIELD_SHORT: Record<string, string> = {
  title: '제목',
  instructor: '강사명',
  categories: '분야',
  level: '대상',
  startDateTime: '시작 일시',
  endDateTime: '종료 일시',
  periodStartDate: '시작일',
  periodEndDate: '종료일',
  sessions: '회차',
  onlineUrl: '접속 링크',
  onlineGuide: '접속 안내',
  address: '장소',
  location: '장소',
  price: '참가비',
  capacity: '정원',
  recruitmentStartDateTime: '모집 기간',
  recruitmentEndDateTime: '모집 기간',
  coupons: '쿠폰',
  thumbnailUrl: '썸네일',
  description: '설명',
};

const StatusBox = styled.nav`
  padding: 6px;
  border: 1px solid ${FORM_COLOR.line};
  border-radius: 14px;
  background: #fff;
`;

const StatusRow = styled('button', { shouldForwardProp: (prop: string) => prop !== 'current' && prop !== 'tone' })<{ current: boolean; tone: 'ok' | 'error' | 'todo' }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 11px 10px;
  border: none;
  border-radius: 10px;
  background: ${({ current }) => (current ? FORM_COLOR.primarySoft : 'none')};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ current }) => (current ? FORM_COLOR.primarySoft : FORM_COLOR.bg)};
  }

  > svg {
    flex: 0 0 auto;
    margin-top: 1px;
    font-size: 20px;
    color: ${({ tone }) => (tone === 'error' ? FORM_COLOR.danger : tone === 'ok' ? FORM_COLOR.primary : '#C3CCC9')};
  }

  > span {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  strong {
    font-size: 14.5px;
    font-weight: ${({ current }) => (current ? 800 : 600)};
    color: ${({ current }) => (current ? FORM_COLOR.primaryDeep : FORM_COLOR.text)};
  }

  em {
    font-style: normal;
    font-size: 12.5px;
    line-height: 1.45;
    word-break: keep-all;
  }

  em.missing {
    color: ${FORM_COLOR.dangerText};
  }

  em.soft {
    color: #b45309;
  }
`;

/**
 * PC 오른쪽 단계 목록 — 5단계 각 줄에 상태(완료 체크 / 지금 단계 강조 / 덜 채움은 빨간 느낌표 + 빠진 항목 짧게).
 * 줄을 누르면 그 단계로(빠진 칸이 있으면 첫 빠진 칸으로). 운영 원칙 동의는 확인 줄 안에.
 */
export const StepStatusList: React.FC<{ isDone: (step: SeminarFormStep) => boolean }> = ({ isDone }) => {
  const { stepProblems, stepMissing, currentStep, goToField, goToStep, needsDirectDealAgree, directDealAgreed } = useSeminarForm();
  const agreeMissing: boolean = needsDirectDealAgree && !directDealAgreed;
  return (
    <StatusBox aria-label="입력 단계">
      {STEP_META.map((meta) => {
        const step: SeminarFormStep = meta.step;
        const problem = stepProblems[step];
        const missingText: string = Array.from(new Set((stepMissing[step] ?? []).map((field) => FIELD_SHORT[field] ?? field))).join('·');
        const lastMissing: boolean = step === 5 && agreeMissing;
        const error: boolean = !!problem || lastMissing;
        const done: boolean = step === 5 ? false : isDone(step);
        const tone: 'ok' | 'error' | 'todo' = error ? 'error' : done ? 'ok' : 'todo';
        const target: string | null = problem ? problem.field : lastMissing ? 'seminar-direct-deal-agree' : null;
        return (
          <StatusRow
            key={step}
            type="button"
            current={currentStep === step}
            tone={tone}
            aria-current={currentStep === step ? 'step' : undefined}
            onClick={() => (target ? goToField(target) : goToStep(step))}
          >
            {error ? <ErrorOutlineIcon aria-hidden /> : done ? <CheckCircleOutlineIcon aria-hidden /> : <RadioButtonUncheckedIcon aria-hidden />}
            <span>
              <strong>{meta.short}</strong>
              {missingText && <em className="missing">{missingText}</em>}
              {lastMissing && <em className="missing">운영 원칙 동의</em>}
            </span>
          </StatusRow>
        );
      })}
    </StatusBox>
  );
};

// ─── 명치사 커리어 운영 원칙(폰·PC 공통, 2026-09-29 사용자 확정 문구) ──────────────

const RuleBox = styled.div`
  padding: 14px 16px;
  border: 1px solid #fecaca;
  border-radius: 14px;
  background: #fef2f2;

  h3 {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 800;
    color: #b91c1c;
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: #7f1d1d;
    white-space: pre-line;
    word-break: keep-all;
  }
`;

const AgreeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 8px 0 -6px -10px;
  font-size: 14px;
  font-weight: 700;
  color: #7f1d1d;
  cursor: pointer;
`;

const DIRECT_DEAL_RULE_LINES: string = [
  '신청·결제는 명치사에서만 받아요.',
  '외부 신청·결제 링크, 계좌이체 안내, 따로 연락해 결제를 유도하면 안 돼요.',
  '명치사는 AI 모니터링 시스템으로 세미나 내용을 확인하고, 모집 중에 수정해서 넣어도 똑같이 확인해요.',
  '어기면 알림 없이 강제 폐강하고 결제한 참가자 전원에게 모두 환불하며, 이후 세미나 등록이 영구히 제한돼요.',
].join('\n');

/** 커리어 운영 원칙 상자 — 커리어로 새로 등록·전환할 때만 필수 동의 체크(이미 커리어인 세미나 수정은 상자만) */
export const DirectDealRuleBox: React.FC = () => {
  const { needsDirectDealAgree, directDealAgreed, setDirectDealAgreed } = useSeminarForm();
  return (
    <RuleBox id="seminar-direct-deal-agree" data-field="seminar-direct-deal-agree">
      <h3>명치사 커리어 운영 원칙</h3>
      <p>{DIRECT_DEAL_RULE_LINES}</p>
      {needsDirectDealAgree && (
        <AgreeLabel>
          <Checkbox
            checked={directDealAgreed}
            onChange={(e) => setDirectDealAgreed(e.target.checked)}
            sx={{ color: '#b91c1c', '&.Mui-checked': { color: '#b91c1c' } }}
          />
          원칙을 확인했고 지키겠습니다 (필수)
        </AgreeLabel>
      )}
    </RuleBox>
  );
};

// ─── 수정 화면 세미나 관리 ──────────────────────────────────────────────

const ManageBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid ${FORM_COLOR.line};
  border-radius: 14px;
  background: #fff;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    color: ${FORM_COLOR.text};
  }
`;

/** 모집 마감·재모집·폐강·판매 종료 — 저장과 성격이 다른 관리 동작이라 저장 버튼과 떨어뜨려 둔다(동작은 그대로) */
export const SeminarManagePanel: React.FC = () => {
  const { isEditMode, openSaleSaved, manage } = useSeminarForm();
  if (!isEditMode || !(manage.showClose || manage.showReopen || manage.showCancel || openSaleSaved)) return null;
  return (
    <ManageBox aria-label="세미나 관리">
      <h3>세미나 관리</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {openSaleSaved && (
          <Button onClick={manage.onToggleSales} disabled={manage.togglingSales} justifycontents="center"
            bgcolor="#fff" color={designSystem.colors.primary} width="auto"
            style={{ border: `1px solid ${designSystem.colors.primary}` }}>
            {manage.togglingSales ? <ButtonSpinner /> : manage.status?.salesClosed ? '판매 다시 시작' : '판매 종료'}
          </Button>
        )}
        {manage.showClose && (
          <Button onClick={manage.onClose} disabled={manage.managing} justifycontents="center"
            bgcolor={designSystem.colors.primary} color="#fff" width="auto">
            {manage.managing ? <ButtonSpinner /> : '모집 마감하기'}
          </Button>
        )}
        {manage.showReopen && (
          <Button onClick={manage.onReopen} disabled={manage.managing} justifycontents="center"
            bgcolor="#fff" color={designSystem.colors.primary} width="auto"
            style={{ border: `1px solid ${designSystem.colors.primary}` }}>
            {manage.managing ? <ButtonSpinner /> : '재모집하기'}
          </Button>
        )}
        {manage.showCancel && (
          <Button onClick={manage.onCancel} disabled={manage.managing} justifycontents="center"
            bgcolor="#fff" color="#dc2626" width="auto" style={{ border: '1px solid #dc2626' }}>
            폐강하기
          </Button>
        )}
      </div>
      {openSaleSaved && (
        <GuideLines>
          {manage.status?.salesClosed
            ? '지금은 판매를 멈춘 상태예요.\n이미 결제한 분은 계속 볼 수 있어요.'
            : '판매를 멈춰도 이미 결제한 분은 계속 볼 수 있어요.'}
        </GuideLines>
      )}
    </ManageBox>
  );
};

// ─── 5단계 ────────────────────────────────────────────────────────────

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/**
 * 5단계 — 목록 카드 미리보기·단계별 완료 체크·(커리어) 운영 원칙 동의·(수정) 세미나 관리.
 * PC 는 오른쪽 칸에 미리보기·단계 목록이 따로 있어 showPreview·showChecklist=false.
 */
const StepConfirm: React.FC<{ showPreview?: boolean; showChecklist?: boolean }> = ({ showPreview = true, showChecklist = true }) => {
  const { career } = useSeminarForm();
  return (
    <Stack>
      {showPreview && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SeminarFormPreview width={210} />
        </div>
      )}
      {showChecklist && <StepChecklist />}
      {career && <DirectDealRuleBox />}
      <SeminarManagePanel />
    </Stack>
  );
};

export default StepConfirm;
